<template>
  <div v-if="isPurchasable.online && isPurchasable.store" data-test="stock-indicator">
    <!-- Stock is unavailable -->
    <div v-if="stockFailed" class="flex items-center text-red-500">
      <CIcon class="text-red-500" name="warning" :title="failedText" />
      <CText
        v-if="isTextVisible"
        el="span"
        :variant="textVariant"
        class="pl-2 overflow-hidden whitespace-nowrap text-ellipsis"
      >
        {{ failedText }}
      </CText>
    </div>
    <!-- Stock is available -->
    <div v-else class="flex flex-col items-end">
      <!-- Reverse determines if indicator should be on the left or right side of the text -->
      <div
        v-if="subAvailabilityDates.length !== 1"
        class="flex items-center gap-1"
        :class="{ 'flex-row-reverse': !isTextVisible }"
      >
        <CIcon
          v-if="displayInfoIcon"
          class="cursor-pointer"
          name="info"
          tabindex="0"
          @click="displayInfobox"
          @keyup.enter="displayInfobox"
        />
        <div class="inline-block align-middle rounded-full h-1.5 w-1.5 m-1.5" :class="[currentStockIconColor]" />
        <CText v-if="inStock || !outOfStockText" el="span" :variant="textVariant">
          {{ quantity }}
        </CText>
        <CText v-if="inStock" el="span" :variant="textVariant">{{ unit }}</CText>
        <CText el="span" :variant="textVariant">{{ labelText }}</CText>
      </div>

      <!-- Stocked amount is less than requested amount on a discontinued product -->
      <div v-if="needsReplacement && requestedQuantity > quantity" class="flex gap-1">
        <div class="inline-block align-middle rounded-full h-1.5 w-1.5 m-1.5 bg-red-500" />
        <CText el="span" :variant="textVariant">
          {{ requestedQuantity - quantity }} {{ unit }} {{ t('cannot_be_delivered') }}
        </CText>
      </div>

      <!-- Display dates where aritcle is re-stocked up until the requested amount is fullfilled  -->
      <div v-for="(stock, index) in subAvailabilityDates" :key="index">
        <div v-if="showStockQuantity(stock) > 0">
          <CText el="span" :variant="textVariant" class="flex gap-1">
            <span class="inline-block align-middle rounded-full h-1.5 w-1.5 m-1.5 bg-orange-400"></span>
            {{ showStockQuantity(stock) }} {{ unit }} {{ t('back_in_stock').toLowerCase() }}:
            {{ formatDate(stock.date) }}
          </CText>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="flex gap-1">
      <div class="flex items-center gap-1" :class="{ 'flex-row-reverse': !isTextVisible }">
        <span class="inline-block align-middle rounded-full h-1.5 w-1.5 m-1.5 bg-gray-400" />
        <CText
          v-if="isTextVisible"
          el="span"
          :variant="textVariant"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
        >
          {{ purchaseConstraintLabel }}
        </CText>
        <CText v-else el="span" :variant="textVariant">0</CText>
      </div>
      <CIcon
        v-if="displayInfoIcon"
        class="cursor-pointer"
        :class="isTextVisible ? 'pl-2' : 'pl-0'"
        name="info"
        tabindex="0"
        @click="displayInfobox"
        @keyup.enter="displayInfobox"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { type CTextVariant } from '@/types/ui';
import { SubAvailability } from '@/types/cart';
import { Availability } from '@/types/stock';

type Props = {
  unit?: string;
  quantity?: number;
  supplyGroup?: string;
  inStockText?: string;
  availability?: Availability | '';
  outOfStockText?: string;
  availabilityDate?: string;
  subAvailabilityDates?: SubAvailability[];
  failedText?: string;
  textVariant?: CTextVariant;
  displayInfoIcon?: boolean;
  needsReplacement?: boolean;
  requestedQuantity?: number;
  isTextVisible?: boolean;
  isPurchasable?: {
    online: boolean;
    store: boolean;
  };
};

const props = withDefaults(defineProps<Props>(), {
  quantity: 0,
  unit: '',
  status: '',
  supplyGroup: '',
  availabilityDate: '',
  subAvailabilityDates: () => [],
  needsReplacement: false,
  requestedQuantity: 0,
  inStockText: '',
  outOfStockText: '',
  failedText: () => 'Kan ej visa lagersaldo',
  displayInfoIcon: false,
  isTextVisible: true,
  availability: '',
  isPurchasable: () => {
    return {
      online: false,
      store: false,
    };
  },
  textVariant: 'system-xs',
});

const emit = defineEmits<{
  (e: 'displayInfobox'): void;
}>();

const { t } = useI18n();

const inStock = computed(() => {
  return props.quantity && props.quantity > 0;
});

const purchaseConstraintLabel = computed(() => {
  if (!props.isPurchasable.online) {
    return t('not_purchasable_online');
  }
  if (!props.isPurchasable.store) {
    return t('not_stocked_in_store');
  }
  return '';
});

/**
 * Since we get dates as non formatted standard string, example: "20210101"
 * We need to split it into date, month, day with regexp.
 * Remove empty strings, and add dash to the string so we get: "2021-01-01"
 */
const formattedDateText = computed(() => {
  if (!props.availabilityDate) return '';
  const splitDateRegexp = /(\d{4})(\d{2})(\d{2})/;
  const date = props.availabilityDate
    .split(splitDateRegexp)
    .filter((s) => s !== '')
    .join('-');
  const formattedDate = formatDate(date);

  return `${t('back_in_stock')}: ${formattedDate}`;
});

const labelText = computed(() => {
  if (inStock.value) {
    return props.inStockText;
  }
  const dateTextCondition = props.availability
    ? props.availability === 'yellow'
    : props.supplyGroup === 'LAG' || props.supplyGroup === '';

  if (props.availabilityDate && dateTextCondition) {
    return formattedDateText.value;
  }

  return props.outOfStockText;
});

const currentStockIconColor = computed(() => {
  if (props.availability) {
    const AVAILABILITY_COLORS = {
      gray: 'bg-gray-400',
      yellow: 'bg-orange-400',
      green: 'bg-green-400',
    };
    return props.subAvailabilityDates.length && props.quantity > 0
      ? AVAILABILITY_COLORS.green
      : AVAILABILITY_COLORS[props.availability];
  }
  switch (props.supplyGroup?.toUpperCase() || '') {
    case 'TRA':
      return 'bg-gray-400';
    default:
      return inStock.value ? 'bg-green-400' : 'bg-orange-400';
  }
});

const stockFailed = computed(() => {
  return props.quantity === -1;
});

const displayInfobox = () => {
  emit('displayInfobox');
};

/**
 * Since we can get in a stock delivery at the same day.
 * We get a result of current stock with the incoming addition.
 * Therefore we subtract the current stock quantity from incoming stock quantity
 * And we get the reminder of the incoming stock.
 */
const showStockQuantity = (stock: SubAvailability) => {
  if (isToday(new Date(stock.date))) {
    return stock.quantity - props.quantity;
  }
  return stock.quantity;
};

/**
 * Create date objects of ISO date strings and check if they are the same day.
 */
const isToday = (backInStockDate: Date) => {
  const today = new Date();
  return (
    today.getFullYear() === backInStockDate.getFullYear() &&
    today.getDate() === backInStockDate.getDate() &&
    today.getMonth() === backInStockDate.getMonth()
  );
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'StockIndicator',
});
</script>
